import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel, Spinner } from "../components/ui-kit";
import { X, ShieldCheck, Plus, UserPlus } from "lucide-react";
import { toast } from "sonner";

const ALL_PERMS = [
  ["gold_research", "Gold Research"], ["btc_research", "Bitcoin Research"], ["live_signals", "Live Signals"],
  ["signal_history", "Signal History"], ["gold_ai", "Gold AI"], ["btc_ai", "Bitcoin AI"],
  ["news_impact", "News Impact"], ["economic_calendar", "Economic Calendar"], ["premium_telegram", "Premium Telegram"],
  ["scalp_gold", "Scalp — Gold"], ["scalp_btc", "Scalp — Bitcoin"],
];
const DURATIONS = [["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["365", "365 days"], ["", "Permanent"]];
const EMPTY_FORM = { name: "", email: "", password: "", country: "", permissions: ["gold_research", "btc_research"], plan: "Free", expiry_days: "30" };

export default function Clients() {
  const [clients, setClients] = useState(null);
  const [sel, setSel] = useState(null);
  const [perms, setPerms] = useState([]);
  const [dur, setDur] = useState("30");
  const [plan, setPlan] = useState("Premium");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => client.get("/clients").then(({ data }) => setClients(data));
  useEffect(() => { load(); }, []);

  const openClient = (c) => { setSel(c); setPerms(c.permissions || []); setPlan(c.plan || "Free"); };
  const toggle = (p) => setPerms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  const toggleForm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  const save = async () => {
    try {
      await client.patch(`/clients/${sel.id}/access`, { permissions: perms, expiry_days: dur ? Number(dur) : null, plan });
      toast.success("Access updated"); setSel(null); load();
    } catch { toast.error("Failed"); }
  };
  const createClient = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password are required"); return; }
    try {
      await client.post("/clients", { ...form, expiry_days: form.expiry_days ? Number(form.expiry_days) : null });
      toast.success("Client created"); setAdding(false); setForm(EMPTY_FORM); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create client"); }
  };

  if (!clients) return <Layout title="Clients"><Spinner /></Layout>;

  return (
    <Layout title="Clients">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-slate-400">{clients.length} clients</span>
        <button data-testid="add-client-btn" onClick={() => { setForm(EMPTY_FORM); setAdding(true); }} className="ml-auto btn-gold px-3 py-1.5 text-xs flex items-center gap-1.5"><UserPlus size={14} />Add Client</button>
      </div>
      <Panel>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[color:var(--border-subtle)]">
              <th className="px-4 py-2.5">Client</th><th className="px-4 py-2.5">Plan</th><th className="px-4 py-2.5">Access Type</th><th className="px-4 py-2.5">Permissions</th><th className="px-4 py-2.5">Expiry</th><th className="px-4 py-2.5"></th>
            </tr></thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-[color:var(--border-subtle)] hover:bg-white/5 transition-colors duration-150" data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-2.5"><div className="text-slate-200">{c.name}</div><div className="text-xs text-slate-500">{c.email}</div></td>
                  <td className="px-4 py-2.5"><span className={c.premium ? "text-gold" : "text-slate-400"}>{c.plan}</span></td>
                  <td className="px-4 py-2.5 text-xs text-slate-400">{c.access_type || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{(c.permissions || []).length} granted</td>
                  <td className="px-4 py-2.5 text-xs font-mono">{c.expiry ? <span className={c.expired ? "text-bear" : "text-slate-300"}>{new Date(c.expiry).toLocaleDateString()}{c.expired && " (expired)"}</span> : <span className="text-slate-500">Permanent</span>}</td>
                  <td className="px-4 py-2.5 text-right"><button data-testid={`manage-${c.id}`} onClick={() => openClient(c)} className="text-xs text-gold hover:underline">Manage</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="client-access-panel">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSel(null)} />
          <div className="relative w-full max-w-md h-full surface overflow-y-auto p-5">
            <div className="flex items-start justify-between mb-4">
              <div><h3 className="font-head text-xl text-white">{sel.name}</h3><div className="text-xs text-slate-500">{sel.email}</div></div>
              <button onClick={() => setSel(null)}><X size={20} className="text-slate-400" /></button>
            </div>

            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Plan</div>
            <div className="flex gap-2 mb-5">{["Free", "Trial", "Premium"].map((p) => (
              <button key={p} onClick={() => setPlan(p)} className={`flex-1 py-2 text-xs border transition-colors duration-200 ${plan === p ? "border-gold text-gold" : "border-[color:var(--border-subtle)] text-slate-400"}`}>{p}</button>
            ))}</div>

            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Feature Permissions</div>
            <div className="grid grid-cols-1 gap-1.5 mb-5">
              {ALL_PERMS.map(([k, label]) => (
                <label key={k} data-testid={`perm-${k}`} className="flex items-center gap-2.5 px-3 py-2 surface cursor-pointer surface-hover transition-colors duration-200">
                  <input type="checkbox" checked={perms.includes(k)} onChange={() => toggle(k)} className="accent-[color:var(--gold)]" />
                  <span className="text-sm text-slate-200">{label}</span>
                </label>
              ))}
            </div>

            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Access Duration</div>
            <select value={dur} onChange={(e) => setDur(e.target.value)} data-testid="access-duration" className="w-full mb-5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
              {DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>

            <button data-testid="save-access" onClick={save} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2"><ShieldCheck size={16} />Save Access</button>
          </div>
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="add-client-panel">
          <div className="absolute inset-0 bg-black/60" onClick={() => setAdding(false)} />
          <div className="relative w-full max-w-md h-full surface overflow-y-auto p-5">
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-head text-xl text-white flex items-center gap-2"><UserPlus size={18} className="text-gold" />New Client</h3>
              <button onClick={() => setAdding(false)}><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="space-y-3 mb-5">
              <input data-testid="new-client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
              <input data-testid="new-client-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
              <input data-testid="new-client-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password" className="w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
              <input data-testid="new-client-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Country (optional)" className="w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Plan</div>
            <div className="flex gap-2 mb-5">{["Free", "Trial", "Premium"].map((p) => (
              <button key={p} onClick={() => setForm({ ...form, plan: p })} className={`flex-1 py-2 text-xs border transition-colors duration-200 ${form.plan === p ? "border-gold text-gold" : "border-[color:var(--border-subtle)] text-slate-400"}`}>{p}</button>
            ))}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Feature Access</div>
            <div className="grid grid-cols-1 gap-1.5 mb-5">
              {ALL_PERMS.map(([k, label]) => (
                <label key={k} data-testid={`new-perm-${k}`} className="flex items-center gap-2.5 px-3 py-2 surface cursor-pointer surface-hover transition-colors duration-200">
                  <input type="checkbox" checked={form.permissions.includes(k)} onChange={() => toggleForm(k)} className="accent-[color:var(--gold)]" />
                  <span className="text-sm text-slate-200">{label}</span>
                </label>
              ))}
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Access Duration</div>
            <select data-testid="new-client-duration" value={form.expiry_days} onChange={(e) => setForm({ ...form, expiry_days: e.target.value })} className="w-full mb-5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
              {DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button data-testid="create-client-btn" onClick={createClient} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2"><Plus size={16} />Create Client</button>
          </div>
        </div>
      )}
    </Layout>
  );
}
